/* ============================================================
   PEMBANTU SHORTCUT "/" — senarai pilihan dalam popup Edit Prompts
   ============================================================
   Taip "/" dalam MANA-MANA kotak teks di dalam `bekasRef` → senarai
   shortcut pelanggan (PRO Studio → Shortcut /) muncul dekat kursor →
   taip untuk tapis → ↑↓ + Enter/Tab atau klik → "/nama" diganti
   dengan teks shortcut, di tempat kursor. (Keputusan user 2026-09-25.)

   ⭐ Kenapa satu pembantu pada BEKAS, bukan prop pada setiap kotak:
      popup Edit Prompts ada ±15 kotak (Hook, Image, Video, Dialog,
      Caption TikTok/FB, lajur scene FLOWNITY…) dalam fail gergasi.
      Pembantu ini mendengar event yang naik (bubble) daripada kotak
      itu, jadi TIADA satu kotak pun perlu disentuh, dan kotak baharu
      yang ditambah kelak terus dapat ciri ini.

   ⭐ Cara teks dimasukkan: `execCommand('insertText')` — browser
      menghantar event `input` biasa, jadi onChange React bagi kotak
      itu berjalan seperti pelanggan menaip sendiri (draf + tanda
      "belum disimpan" + penyegerak Hook⇄Image semua ikut). ⚠️ Ctrl+Z
      TIDAK terbukti: diuji 26 Sept, kotak popup ini tidak membatalkan
      taipan biasa pun (bukan kesan ciri ini). Sandaran bila execCommand ditolak:
      setter nilai asli + event `input` (React masih menangkapnya).

   Pemicu: "/" di AWAL kotak atau SELEPAS jarak/baris baru sahaja —
   supaya "1/2", "9:16/4K" atau link dalam prompt tidak membuka senarai.
   ============================================================ */

import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

import { useSenaraiShortcut } from '../hooks/useSenaraiShortcut';
import { useCiriPercuma } from '../hooks/useCiriPercuma';
import PopupCiriPercuma from './PopupCiriPercuma';
import { CIRI, bolehGunaCiri } from '../utils/ciriPercuma';

const CORAK_PEMICU = /(^|\s)\/([a-z0-9_]*)$/i;
const LEBAR_MENU = 340;
const MAKS_PAPAR = 8;

/* Kedudukan piksel kursor dalam textarea — teknik "cermin": salin gaya
   teks ke div tersembunyi, isi teks sehingga kursor, ukur span penanda. */
function kedudukanKursor(el, pos) {
    const gaya = window.getComputedStyle(el);
    const cermin = document.createElement('div');
    const salin = ['boxSizing', 'width', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
        'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
        'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'letterSpacing', 'lineHeight',
        'textTransform', 'wordSpacing', 'textIndent', 'tabSize'];
    salin.forEach((k) => { cermin.style[k] = gaya[k]; });
    Object.assign(cermin.style, {
        position: 'absolute', visibility: 'hidden', top: '0', left: '-9999px',
        whiteSpace: 'pre-wrap', wordWrap: 'break-word', overflow: 'hidden',
    });
    cermin.textContent = el.value.slice(0, pos);
    const penanda = document.createElement('span');
    penanda.textContent = '​';
    cermin.appendChild(penanda);
    document.body.appendChild(cermin);
    const kotak = el.getBoundingClientRect();
    const tinggiBaris = parseFloat(gaya.lineHeight) || parseFloat(gaya.fontSize) * 1.4 || 18;
    const x = kotak.left + penanda.offsetLeft - el.scrollLeft;
    const y = kotak.top + penanda.offsetTop - el.scrollTop + tinggiBaris;
    document.body.removeChild(cermin);
    return {
        x: Math.min(Math.max(kotak.left, x), kotak.right),
        y: Math.min(Math.max(kotak.top, y), kotak.bottom),
    };
}

function masukkanTeks(el, mula, akhir, teks) {
    el.focus();
    el.setSelectionRange(mula, akhir);
    let jadi = false;
    try { jadi = document.execCommand('insertText', false, teks); } catch { jadi = false; }
    if (!jadi || el.value.slice(mula, mula + teks.length) !== teks) {
        const setter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set;
        const baru = el.value.slice(0, mula) + teks + el.value.slice(akhir);
        if (setter) setter.call(el, baru); else el.value = baru;
        el.dispatchEvent(new Event('input', { bubbles: true }));
        requestAnimationFrame(() => {
            try { el.setSelectionRange(mula + teks.length, mula + teks.length); } catch { /* kotak sudah hilang */ }
        });
    }
}

export default function ShortcutSlashPembantu({ bekasRef, t }) {
    const { senarai, sudahMuat } = useSenaraiShortcut(true);
    /* 🔒 Ciri PRO Studio — wajib claim percuma (hingga 30 Nov 2026). Belum
       claim / tamat: "/" tetap membuka kotak, tetapi ia memberitahu cara
       mengaktifkan, bukan memasukkan teks. */
    const ciriP = useCiriPercuma(true);
    const statusSc = ciriP.status(CIRI.SHORTCUT);
    const terkunci = ciriP.sudahMuat && !bolehGunaCiri(statusSc);
    const [menu, setMenu] = useState(null);   // { el, mula, akhir, q, x, y }
    const [pilih, setPilih] = useState(0);
    const [popup, setPopup] = useState(false);

    const T = (kunci, lalai, vars) => {
        if (!t) return lalai;
        const hasil = t(kunci, vars);
        return (!hasil || hasil === kunci) ? lalai : hasil;
    };

    const bolehPakai = terkunci ? [] : senarai;
    const q = menu?.q || '';
    const padan = bolehPakai
        .filter((x) => x.nama.includes(q))
        .sort((a, b) => (Number(b.nama.startsWith(q)) - Number(a.nama.startsWith(q))) || a.nama.localeCompare(b.nama))
        .slice(0, MAKS_PAPAR);

    /* Ref supaya pendengar DOM (dipasang sekali) sentiasa membaca nilai
       terkini tanpa dipasang semula pada setiap render. */
    const kini = useRef({ menu: null, padan: [], pilih: 0 });
    useLayoutEffect(() => { kini.current = { menu, padan, pilih }; });

    const tutup = useCallback(() => { setMenu(null); setPilih(0); }, []);

    const kemaskini = useCallback((el) => {
        if (!el || el.tagName !== 'TEXTAREA' || el.readOnly || el.disabled) { tutup(); return; }
        const pos = el.selectionStart;
        if (pos !== el.selectionEnd) { tutup(); return; }
        const m = el.value.slice(0, pos).match(CORAK_PEMICU);
        if (!m) { tutup(); return; }
        const qBaru = m[2].toLowerCase();
        const mula = pos - m[2].length - 1;
        const { x, y } = kedudukanKursor(el, pos);
        const lama = kini.current.menu;
        if (!lama || lama.q !== qBaru) setPilih(0);
        setMenu({ el, mula, akhir: pos, q: qBaru, x, y });
    }, [tutup]);

    const guna = useCallback((item) => {
        const m = kini.current.menu;
        if (!m || !item) return;
        masukkanTeks(m.el, m.mula, m.akhir, item.teks);
        tutup();
    }, [tutup]);

    useEffect(() => {
        const bekas = bekasRef?.current;
        if (!bekas) return undefined;

        const onInput = (e) => kemaskini(e.target);
        const onKeyUp = (e) => {
            if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) kemaskini(e.target);
        };
        const onClick = (e) => { if (e.target.tagName === 'TEXTAREA') kemaskini(e.target); else tutup(); };
        /* Fasa CAPTURE — kekunci navigasi ditangkap sebelum kotak (atau
           pengendali lain dalam popup) melihatnya, hanya bila senarai terbuka. */
        const onKeyDown = (e) => {
            const { menu: m, padan: p, pilih: i } = kini.current;
            if (!m || e.target !== m.el) return;
            if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); tutup(); return; }
            if (!p.length) return;
            if (e.key === 'ArrowDown') { e.preventDefault(); e.stopPropagation(); setPilih((i + 1) % p.length); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); e.stopPropagation(); setPilih((i - 1 + p.length) % p.length); }
            else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); e.stopPropagation(); guna(p[i]); }
        };
        const onFocusOut = () => setTimeout(() => {
            const m = kini.current.menu;
            if (m && document.activeElement !== m.el) tutup();
        }, 120);
        /* Kotak / popup ditatal (termasuk tatal automatik semasa menaip di
           hujung kotak) → kira semula kedudukan, jangan tutup. */
        const onScroll = () => { const m = kini.current.menu; if (m) kemaskini(m.el); };

        bekas.addEventListener('input', onInput);
        bekas.addEventListener('keyup', onKeyUp);
        bekas.addEventListener('click', onClick);
        bekas.addEventListener('keydown', onKeyDown, true);
        bekas.addEventListener('focusout', onFocusOut);
        bekas.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onScroll);
        return () => {
            bekas.removeEventListener('input', onInput);
            bekas.removeEventListener('keyup', onKeyUp);
            bekas.removeEventListener('click', onClick);
            bekas.removeEventListener('keydown', onKeyDown, true);
            bekas.removeEventListener('focusout', onFocusOut);
            bekas.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onScroll);
        };
    }, [bekasRef, kemaskini, guna, tutup]);

    const popupEl = popup ? <PopupCiriPercuma ciri={CIRI.SHORTCUT} onTutup={() => setPopup(false)} t={t} /> : null;
    if (!menu) return popupEl;

    /* Letak di bawah kursor; terbalik ke atas bila tiada ruang di bawah. */
    const tinggiAnggar = Math.min(Math.max(padan.length, 1), MAKS_PAPAR) * 44 + 40;
    const keAtas = menu.y + tinggiAnggar > window.innerHeight - 8;
    const kiri = Math.max(8, Math.min(menu.x, window.innerWidth - LEBAR_MENU - 8));
    const kedudukan = keAtas
        ? { left: kiri, bottom: window.innerHeight - menu.y + 24 }
        : { left: kiri, top: menu.y + 4 };

    const warna = {
        latar: 'var(--s-panel, #ffffff)', garis: 'var(--s-line-strong, #d6d3cc)',
        dakwat: 'var(--s-ink, #1f1e1b)', pudar: 'var(--s-ink-3, #8a877f)',
        pilih: 'var(--s-fill, #f1efe8)',
    };
    const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' };

    let isi;
    if (terkunci) {
        isi = (
            <div style={{ padding: '10px 12px', color: warna.pudar, lineHeight: 1.5 }}>
                {statusSc === 'tamat'
                    ? T('ws.sc.lockedTamat', 'Tempoh percuma Shortcut / telah tamat.')
                    : (
                        <>
                            {T('ws.sc.lockedBelum', 'Shortcut / percuma sehingga 30 November 2026.')}{' '}
                            <span
                                role="button"
                                onMouseDown={(e) => { e.preventDefault(); setPopup(true); tutup(); }}
                                style={{ color: warna.dakwat, textDecoration: 'underline', cursor: 'pointer' }}
                            >
                                {T('ws.sc.activate', 'Aktifkan sekarang')}
                            </span>
                        </>
                    )}
            </div>
        );
    } else if (!sudahMuat) {
        isi = <div style={{ padding: '10px 12px', color: warna.pudar }}>{T('ws.sc.loading', 'Memuatkan shortcut…')}</div>;
    } else if (senarai.length === 0) {
        isi = (
            <div style={{ padding: '10px 12px', color: warna.pudar, lineHeight: 1.5 }}>
                {T('ws.sc.empty', 'Belum ada shortcut. Cipta di PRO Studio → Shortcut /.')}
            </div>
        );
    } else if (padan.length === 0) {
        isi = <div style={{ padding: '10px 12px', color: warna.pudar }}>{T('ws.sc.noMatch', 'Tiada shortcut sepadan.')}</div>;
    } else {
        isi = padan.map((item, i) => (
            <div
                key={item.id}
                role="option"
                aria-selected={i === pilih}
                onMouseDown={(e) => { e.preventDefault(); guna(item); }}
                onMouseEnter={() => setPilih(i)}
                style={{
                    padding: '7px 10px', borderRadius: 7, cursor: 'pointer',
                    background: i === pilih ? warna.pilih : 'transparent',
                }}
            >
                <div style={{ ...mono, fontSize: 13, color: warna.dakwat }}>/{item.nama}</div>
                <div style={{
                    fontSize: 12, color: warna.pudar, marginTop: 1,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{item.teks}</div>
            </div>
        ));
    }

    return createPortal(
        <>
        {popupEl}
        <div
            role="listbox"
            onMouseDown={(e) => e.preventDefault()}
            style={{
                position: 'fixed', zIndex: 10070, width: LEBAR_MENU, maxWidth: 'calc(100vw - 16px)',
                ...kedudukan,
                background: warna.latar, color: warna.dakwat,
                border: `1px solid ${warna.garis}`, borderRadius: 10,
                boxShadow: '0 8px 24px rgba(0,0,0,0.14)', padding: 4,
                fontSize: 13, fontFamily: 'inherit',
            }}
        >
            {isi}
            {padan.length > 0 && (
                <div style={{ padding: '4px 10px 2px', fontSize: 11, color: warna.pudar }}>
                    {T('ws.sc.hint', '↑↓ pilih · Enter masukkan · Esc tutup')}
                </div>
            )}
        </div>
        </>,
        document.body
    );
}
